import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { withRequestTimeout } from "@/lib/with-request-timeout";

/**
 * Returns the caller's set of 5-way platform permissions (P6, May 2026):
 * `tenant_manager`, `feature_manager`, `impersonation_access`,
 * `billing_access`, `audit_access`.
 *
 * Source: `GET /api/me/platform-permissions` (returns `[]` for any caller
 * who isn't a platform operator). 60s cache, same cadence as
 * `useIsPlatformOperator`. Cheap server call.
 *
 * Inline customFetch — the per-tenant-roles ask landed before
 * OpenAPI codegen could pick this endpoint up; in a follow-up turn this
 * can swap to an orval-generated hook with no FE call-site changes.
 */
export type PlatformPermission =
  | "tenant_manager"
  | "feature_manager"
  | "impersonation_access"
  | "billing_access"
  | "audit_access"
  | "website_manager"
  | "website_editor"
  | "website_publisher"
  | "manage_website_enquiries"
  | "manage_plans_and_pricing"
  | "approve_tenant_trials"
  | "convert_enquiry_to_tenant"
  | "manage_support_tickets"
  // Memberships Console (Task #1149)
  | "manage_memberships"
  | "manage_billing"
  | "manage_invoices"
  | "manage_payments"
  | "manage_trials"
  | "manage_subscriptions"
  | "manage_plans"
  | "manage_usage_limits"
  | "impersonate_client_users"
  | "view_tenant_dashboard"
  // Tenants Dashboard Phase 2 (Task #1355)
  | "view_service_peaks"
  | "view_feature_usage"
  | "view_tenant_health_detail"
  | "view_billing_summary"
  | "manage_admin_notes"
  // Background Jobs console (Task #1799)
  | "manage_background_jobs"
  // Performance Centre (Task #1815)
  | "view_performance_centre"
  // Help Analytics (Task #2088)
  | "view_help_analytics"
  | "view_market_data"
  | "manage_market_data";

interface PlatformPermissionsResponse {
  permissions: PlatformPermission[];
}

export function useMyPlatformPermissions(): {
  permissions: PlatformPermission[];
  isLoading: boolean;
  /** True when the permissions lookup itself failed (network/server error) —
   * callers should show an error state, NOT an "access restricted" state. */
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
} {
  const q = useQuery<PlatformPermissionsResponse>({
    queryKey: ["/api/me/platform-permissions"],
    queryFn: ({ signal }) => withRequestTimeout(
      async (requestSignal) => {
        const res = await customFetch<PlatformPermissionsResponse>(
          "/api/me/platform-permissions",
          { method: "GET", responseType: "json", signal: requestSignal },
        );
        return res ?? { permissions: [] };
      },
      {
        signal,
        timeoutMessage: "Market-data permissions could not be checked within 15 seconds.",
      },
    ),
    retry: false,
    staleTime: 60_000,
  });
  return {
    permissions: q.data?.permissions ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}
